import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute} from "@angular/router";
import {HttpErrorResponse} from "@angular/common/http";
import {CdkCopyToClipboard} from "@angular/cdk/clipboard";
import {MatDialog} from "@angular/material/dialog";
import {MatIcon} from "@angular/material/icon";
import {MatSnackBar} from "@angular/material/snack-bar";
import {MatTooltip} from "@angular/material/tooltip";
import {saveAs} from "file-saver-es";
import {HubEnvironmentOption} from "../../../model/gateway/hubEnvironmentOption";
import {GatewayOrder} from "../../../model/gateway/gatewayOrder";
import {GatewayOrderDevice} from "../../../model/gateway/gatewayOrderDevice";
import {GatewayOrderService} from "../../../service/gateway.order.service";
import {GatewayOrderEditNameComponent} from "../gateway-order-edit-name/gateway-order-edit-name.component";

const JSON_SPACING = 2;
const EDIT_GATEWAY_ORDER_NAME_WIDTH = '40%';
const IMPORT_SUCCESS_DURATION_MS = 5000;
const REFRESH_INTERVAL_MS = 5000;
const SNACKBAR_DURATION_MS = 3000;

@Component({
  selector: 'app-gateway-order-detail',
  standalone: true,
  imports: [
    CdkCopyToClipboard,
    MatIcon,
    MatTooltip
  ],
  templateUrl: './gateway-order-detail.component.html',
  styleUrl: './gateway-order-detail.component.css'
})
export class GatewayOrderDetailComponent implements OnInit, OnDestroy {

  private gatewayOrderService = inject(GatewayOrderService);
  private route = inject(ActivatedRoute);
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);

  gatewayOrder: GatewayOrder | undefined;
  selectedFile: File | undefined;
  uploadingDevices = false;
  refreshingDevices = false;
  refreshDevicesFailed = false;
  retryingSelectedDevices = false;
  deletingSelectedDevices = false;
  downloadingSecureInReachConfigs = false;
  importError = "";
  importSuccess = "";
  retryDevicesError = "";
  deleteDevicesError = "";
  downloadSecureInReachConfigsError = "";
  hubEnvironmentOptions: HubEnvironmentOption[] = [];
  selectedGatewayOrderDeviceIds = new Set<number>();
  private importSuccessTimeout: ReturnType<typeof setTimeout> | undefined;
  private refreshInterval: ReturnType<typeof setInterval> | undefined;

  ngOnInit() {
    this.route.params.subscribe(params => {
      const gatewayOrderId = Number(params['id']);
      this.getGatewayOrder(gatewayOrderId);
      this.startRefreshInterval();
    });
    this.loadHubEnvironments();
  }

  ngOnDestroy() {
    this.clearImportSuccess();
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = undefined;
    }
  }

  get environmentKey(): string {
    return this.gatewayOrder?.gatewayEnvironment?.environmentKey || this.gatewayOrder?.environmentKey || '';
  }

  get hubEnvironmentLabel(): string {
    const hubTenantName = this.gatewayOrder?.hubTenantName;
    if (!hubTenantName) {
      return '';
    }

    return this.hubEnvironmentOptions.find(option => option.value === hubTenantName)?.label || hubTenantName;
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    this.selectedFile = input.files && input.files.length > 0 ? input.files[0] : undefined;
    this.importError = "";
    this.clearImportSuccess();
  }

  uploadDevices(fileInput?: HTMLInputElement) {
    if (!this.gatewayOrder || !this.selectedFile || this.uploadingDevices) {
      return;
    }

    const fileName = this.selectedFile.name;
    this.uploadingDevices = true;
    this.importError = "";
    this.clearImportSuccess();
    this.gatewayOrderService.importDevices(this.gatewayOrder.gatewayOrderId, this.selectedFile).subscribe({
      next: gatewayOrder => {
        this.setGatewayOrder(gatewayOrder);
        this.showImportSuccess(`${fileName} uploaded successfully.`);
        this.selectedFile = undefined;
        if (fileInput) {
          fileInput.value = "";
        }
        this.uploadingDevices = false;
      },
      error: err => {
        if (err instanceof HttpErrorResponse) {
          console.log(err.status + ' ' + err.message);
          this.importError = Array.isArray(err.error) ? err.error.join(' ') : err.error || err.message;
        }
        this.uploadingDevices = false;
      }
    });
  }

  editGatewayOrderName() {
    if (!this.gatewayOrder) {
      return;
    }

    const dialogRef = this.dialog.open(GatewayOrderEditNameComponent, {
      width: EDIT_GATEWAY_ORDER_NAME_WIDTH
    });
    dialogRef.componentInstance.setGatewayOrder(this.gatewayOrder);

    dialogRef.afterClosed().subscribe(() => {
      if (this.gatewayOrder) {
        this.getGatewayOrder(this.gatewayOrder.gatewayOrderId);
      }
    });
  }

  refreshDevices() {
    if (!this.gatewayOrder || this.refreshingDevices) {
      return;
    }

    this.refreshingDevices = true;
    this.refreshDevicesFailed = false;
    this.gatewayOrderService.findById(this.gatewayOrder.gatewayOrderId).subscribe({
      next: gatewayOrder => {
        this.setGatewayOrder(gatewayOrder);
        this.refreshingDevices = false;
      },
      error: err => {
        if (err instanceof HttpErrorResponse) {
          console.log(err.status + ' ' + err.message);
        }
        this.refreshDevicesFailed = true;
        this.refreshingDevices = false;
      }
    });
  }

  isActionableDevice(device: GatewayOrderDevice) {
    return device.gatewayOrderDeviceStatus === 'READY'
      || (device.gatewayOrderDeviceStatus?.startsWith('FAILED') ?? false);
  }

  isDeviceSelected(device: GatewayOrderDevice) {
    return this.selectedGatewayOrderDeviceIds.has(device.gatewayOrderDeviceId);
  }

  selectDevice(device: GatewayOrderDevice, event: Event) {
    if (!this.isActionableDevice(device)) {
      return;
    }

    const input = event.target as HTMLInputElement;
    if (input.checked) {
      this.selectedGatewayOrderDeviceIds.add(device.gatewayOrderDeviceId);
    } else {
      this.selectedGatewayOrderDeviceIds.delete(device.gatewayOrderDeviceId);
    }
  }

  selectAllActionableDevices(event: Event) {
    const input = event.target as HTMLInputElement;
    const actionableDevices = this.getActionableDevices();

    if (input.checked) {
      actionableDevices.forEach(device => this.selectedGatewayOrderDeviceIds.add(device.gatewayOrderDeviceId));
    } else {
      actionableDevices.forEach(device => this.selectedGatewayOrderDeviceIds.delete(device.gatewayOrderDeviceId));
    }
  }

  allActionableDevicesSelected() {
    const actionableDevices = this.getActionableDevices();

    return actionableDevices.length > 0
      && actionableDevices.every(device => this.selectedGatewayOrderDeviceIds.has(device.gatewayOrderDeviceId));
  }

  hasActionableDevices() {
    return this.getActionableDevices().length > 0;
  }

  devicesSelected() {
    return this.selectedGatewayOrderDeviceIds.size > 0;
  }

  hasSecureInReachDevices() {
    return (this.gatewayOrder?.gatewayOrderDevices || []).some(device => device.secureInReach);
  }

  createOrRetrySelectedDevices() {
    if (!this.gatewayOrder || !this.devicesSelected() || this.retryingSelectedDevices || this.deletingSelectedDevices) {
      return;
    }

    this.retryingSelectedDevices = true;
    this.retryDevicesError = "";
    this.deleteDevicesError = "";
    this.gatewayOrderService.createOrRetryDevices(
      this.gatewayOrder.gatewayOrderId,
      Array.from(this.selectedGatewayOrderDeviceIds)
    ).subscribe({
      next: gatewayOrder => {
        this.setGatewayOrder(gatewayOrder);
        this.retryingSelectedDevices = false;
      },
      error: err => {
        if (err instanceof HttpErrorResponse) {
          console.log(err.status + ' ' + err.message);
          this.retryDevicesError = Array.isArray(err.error) ? err.error.join(' ') : err.error || err.message;
        }
        this.retryingSelectedDevices = false;
      }
    });
  }

  downloadSecureInReachConfigs() {
    if (!this.gatewayOrder || !this.hasSecureInReachDevices() || this.downloadingSecureInReachConfigs) {
      return;
    }

    this.downloadingSecureInReachConfigs = true;
    this.downloadSecureInReachConfigsError = "";
    this.gatewayOrderService.downloadSecureInReachConfigs(this.gatewayOrder.gatewayOrderId).subscribe({
      next: configs => {
        const blob = new Blob([configs], {
          type: 'application/zip'
        });

        saveAs(blob, "secure_inReach_Configs_" + Date.now() + ".zip");
        this.downloadingSecureInReachConfigs = false;
      },
      error: err => {
        if (err instanceof HttpErrorResponse) {
          console.error(err.status + ' ' + err.message);
        }
        this.downloadSecureInReachConfigsError = 'Could not download secure inReach configs.';
        this.downloadingSecureInReachConfigs = false;
      }
    });
  }

  deleteSelectedDevices() {
    if (!this.gatewayOrder || !this.devicesSelected() || this.deletingSelectedDevices || this.retryingSelectedDevices) {
      return;
    }

    this.deletingSelectedDevices = true;
    this.deleteDevicesError = "";
    this.retryDevicesError = "";
    this.gatewayOrderService.deleteDevices(
      this.gatewayOrder.gatewayOrderId,
      Array.from(this.selectedGatewayOrderDeviceIds)
    ).subscribe({
      next: gatewayOrder => {
        this.selectedGatewayOrderDeviceIds.clear();
        this.setGatewayOrder(gatewayOrder);
        this.deletingSelectedDevices = false;
      },
      error: err => {
        if (err instanceof HttpErrorResponse) {
          console.log(err.status + ' ' + err.message);
          this.deleteDevicesError = Array.isArray(err.error) ? err.error.join(' ') : err.error || err.message;
        }
        this.deletingSelectedDevices = false;
      }
    });
  }

  copyDeviceJsonToClipboard(device: GatewayOrderDevice) {
    return JSON.stringify(device, null, JSON_SPACING);
  }

  confirmDeviceJsonCopied() {
    this.snackBar.open('Gateway Order Device copied to clipboard', undefined, {
      duration: SNACKBAR_DURATION_MS
    });
  }

  private getGatewayOrder(gatewayOrderId: number) {
    this.gatewayOrderService.findById(gatewayOrderId).subscribe({
      next: gatewayOrder => this.setGatewayOrder(gatewayOrder),
      error: err => {
        if (err instanceof HttpErrorResponse) {
          console.log(err.status + ' ' + err.message);
        }
      }
    });
  }

  private loadHubEnvironments() {
    this.gatewayOrderService.findHubEnvironments().subscribe({
      next: hubEnvironmentOptions => this.hubEnvironmentOptions = hubEnvironmentOptions,
      error: err => {
        if (err instanceof HttpErrorResponse) {
          console.log(err.status + ' ' + err.message);
        }
      }
    });
  }

  private setGatewayOrder(gatewayOrder: GatewayOrder) {
    this.gatewayOrder = gatewayOrder;
    this.pruneSelectedDevices();
  }

  private startRefreshInterval() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }

    this.refreshInterval = setInterval(() => this.refreshDevices(), REFRESH_INTERVAL_MS);
  }

  private showImportSuccess(message: string) {
    this.clearImportSuccess();
    this.importSuccess = message;
    this.importSuccessTimeout = setTimeout(() => this.clearImportSuccess(), IMPORT_SUCCESS_DURATION_MS);
  }

  private clearImportSuccess() {
    this.importSuccess = "";
    if (this.importSuccessTimeout) {
      clearTimeout(this.importSuccessTimeout);
      this.importSuccessTimeout = undefined;
    }
  }

  private getActionableDevices() {
    return (this.gatewayOrder?.gatewayOrderDevices || []).filter(device => this.isActionableDevice(device));
  }

  private pruneSelectedDevices() {
    const actionableDeviceIds = new Set(this.getActionableDevices().map(device => device.gatewayOrderDeviceId));
    this.selectedGatewayOrderDeviceIds.forEach(gatewayOrderDeviceId => {
      if (!actionableDeviceIds.has(gatewayOrderDeviceId)) {
        this.selectedGatewayOrderDeviceIds.delete(gatewayOrderDeviceId);
      }
    });
  }
}
